import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Tabs,
    Tab,
    TextField,
    ButtonBase,
    Box,
    Typography,
} from "@mui/material";
import { setTestSettings } from "../../redux/actions/userAction";

const TabPanel = ({ children, value, index }) => (
    <div role="tabpanel" hidden={value !== index}>
        {value === index && <Box p={2}>{children}</Box>}
    </div>
);

const InputField = ({ label, name, value, onChange, type = "text" }) => (
    <TextField
        type={type}
        label={label}
        name={name}
        value={value}
        onChange={onChange}
        variant="outlined"
        fullWidth
        margin="normal"
    />
);

const TestConfig = () => {
    const dispatch = useDispatch();
    const { testConfig } = useSelector((state) => state.User);
    const [tabIndex, setTabIndex] = useState(0);

    const onChange = (e) => {
        dispatch(
            setTestSettings({ name: e.target.name, value: e.target.value })
        );
    };

    return (
        <Box>
            <Tabs
                value={tabIndex}
                onChange={(_, newIndex) => setTabIndex(newIndex)}>
                <Tab label="Referral" />
                <Tab label="Crypto Fees" />
                <Tab label="Virtual Cards" />
                <Tab label="Wallet Limits" />
                <Tab label="General" />
                <Tab label="Bank" />
            </Tabs>

            {/* Referral Settings */}
            <TabPanel value={tabIndex} index={0}>
                <Typography variant="h6">Referral Settings</Typography>
                <Box display="flex" gap={2} my={2}>
                    <ButtonBase
                        onClick={() =>
                            onChange({
                                target: {
                                    name: "REFERRAL_ACTIVE",
                                    value: true,
                                },
                            })
                        }
                        className={`config-env-item ${
                            testConfig.REFERRAL_ACTIVE && "selected"
                        }`}>
                        Activated
                    </ButtonBase>
                    <ButtonBase
                        onClick={() =>
                            onChange({
                                target: {
                                    name: "REFERRAL_ACTIVE",
                                    value: false,
                                },
                            })
                        }
                        className={`config-env-item ${
                            !testConfig.REFERRAL_ACTIVE && "selected"
                        }`}>
                        Deactivated
                    </ButtonBase>
                </Box>
                <InputField
                    label="Referral Percentage"
                    name="REFERRAL_PERCENTAGE"
                    value={testConfig.REFERRAL_PERCENTAGE}
                    onChange={onChange}
                />
                <InputField
                    label="Referral Transaction Count"
                    name="REFERRAL_TRANSACTION_COUNT"
                    value={testConfig.REFERRAL_TRANSACTION_COUNT}
                    onChange={onChange}
                />
                <InputField
                    label="Referral Bonus Amount"
                    name="REFERRAL_BONUS_AMOUNT"
                    value={testConfig.REFERRAL_BONUS_AMOUNT}
                    onChange={onChange}
                />
                <InputField
                    label="Referral Reward"
                    name="referralReward"
                    value={testConfig.referralReward}
                    onChange={onChange}
                />
                <InputField
                    label="Required Transactions"
                    name="requiredTransactions"
                    value={testConfig.requiredTransactions}
                    onChange={onChange}
                />
                <InputField
                    label="Custom Referral Bonus"
                    name="customReferralBonus"
                    value={testConfig.customReferralBonus}
                    onChange={onChange}
                />
                <InputField
                    label="Custom Required Transactions"
                    name="customRequiredTransactions"
                    value={testConfig.customRequiredTransactions}
                    onChange={onChange}
                />
                <InputField
                    label="Custom Referral Code"
                    name="customReferralCode"
                    value={testConfig.customReferralCode}
                    onChange={onChange}
                />
            </TabPanel>

            {/* Crypto Fees */}
            <TabPanel value={tabIndex} index={1}>
                <Typography variant="h6">Crypto Fees</Typography>
                <InputField
                    label="USDT Fee"
                    name="USDT_FEE"
                    value={testConfig.USDT_FEE}
                    onChange={onChange}
                />
                <InputField
                    label="ETH Fee"
                    name="ETH_FEE"
                    value={testConfig.ETH_FEE}
                    onChange={onChange}
                />
                <InputField
                    label="BTC Fee"
                    name="BTC_FEE"
                    value={testConfig.BTC_FEE}
                    onChange={onChange}
                    type="number"
                />
                <InputField
                    label="BTC Fee > $200"
                    name="BTC_FEE_GRATER_THAN_200_USD"
                    value={testConfig.BTC_FEE_GRATER_THAN_200_USD}
                    onChange={onChange}
                />
                <InputField
                    label="BTC Fee < $200"
                    name="BTC_FEE_LESS_THAN_200_USD"
                    value={testConfig.BTC_FEE_LESS_THAN_200_USD}
                    onChange={onChange}
                />
                <InputField
                    label="CoinMarketCap API Key"
                    name="COINMARKETCAP_API_KEY"
                    value={testConfig.COINMARKETCAP_API_KEY}
                    onChange={onChange}
                />
            </TabPanel>

            {/* Virtual Card Settings */}
            <TabPanel value={tabIndex} index={2}>
                <Typography variant="h6">Virtual Card Settings</Typography>
                <InputField
                    label="Min Deposit (USD)"
                    name="MINIMUM_CARD_DEPOSIT"
                    value={testConfig.MINIMUM_CARD_DEPOSIT}
                    onChange={onChange}
                />
                <InputField
                    label="Min Withdraw (USD)"
                    name="MINIMUM_CARD_WITHDRAW"
                    value={testConfig.MINIMUM_CARD_WITHDRAW}
                    onChange={onChange}
                />
                <InputField
                    label="Creation Fees"
                    name="CARD_CREATION_FEES"
                    value={testConfig.CARD_CREATION_FEES}
                    onChange={onChange}
                />
                <InputField
                    label="Deposit Fees (NGN)"
                    name="CARD_DEPOSIT_FEES"
                    value={testConfig.CARD_DEPOSIT_FEES}
                    onChange={onChange}
                />
                <InputField
                    label="Withdraw Fees (NGN)"
                    name="CARD_WITHDRAW_FEES"
                    value={testConfig.CARD_WITHDRAW_FEES}
                    onChange={onChange}
                />
                <InputField
                    label="Withdraw Admin Profit (%)"
                    name="VIRTUAL_CARD_WITHDRAW_ADMIN_PROFIT"
                    value={testConfig.VIRTUAL_CARD_WITHDRAW_ADMIN_PROFIT}
                    onChange={onChange}
                />
                <InputField
                    label="Card Max Limit"
                    name="CARD_MAXIMUM_LIMIT"
                    value={testConfig.CARD_MAXIMUM_LIMIT}
                    onChange={onChange}
                />
            </TabPanel>

            {/* Wallet Limits */}
            <TabPanel value={tabIndex} index={3}>
                <Typography variant="h6">Wallet Limits</Typography>
                <InputField
                    label="Max Wallet Amount (%)"
                    name="MAXIMUM_WALLET_AMOUNT_PER"
                    value={testConfig.MAXIMUM_WALLET_AMOUNT_PER}
                    onChange={onChange}
                />
                <InputField
                    label="Min Send USD"
                    name="MIN_Send_USD"
                    value={testConfig.MIN_Send_USD}
                    onChange={onChange}
                />
                <InputField
                    label="Min Sell USD"
                    name="MIN_Sell_USD"
                    value={testConfig.MIN_Sell_USD}
                    onChange={onChange}
                />
                <InputField
                    label="Min Buy USD"
                    name="MIN_BUY_USD"
                    value={testConfig.MIN_BUY_USD}
                    onChange={onChange}
                />
                <InputField
                    label="Min Buy NGN"
                    name="MIN_BUY_NGN"
                    value={testConfig.MIN_BUY_NGN}
                    onChange={onChange}
                />
                <InputField
                    label="Sell USD Rate"
                    name="USD_RATE"
                    value={testConfig.USD_RATE}
                    onChange={onChange}
                />
                <InputField
                    label="Buy BTC USD Rate"
                    name="BUY_BTC_USD_RATE"
                    value={testConfig.BUY_BTC_USD_RATE}
                    onChange={onChange}
                />
                <InputField
                    label="Virtual Card USD Rate"
                    name="VIRTUAL_CARD_USD_RATE"
                    value={testConfig.VIRTUAL_CARD_USD_RATE}
                    onChange={onChange}
                />
            </TabPanel>

            {/* General Settings */}
            <TabPanel value={tabIndex} index={4}>
                <Typography variant="h6">General Settings</Typography>
                <InputField
                    label="OTP Duration (days)"
                    name="OTP_DURATION"
                    value={testConfig.OTP_DURATION}
                    onChange={onChange}
                />
                <InputField
                    label="BTC Explorer URL"
                    name="BTC_EXPLORER"
                    value={testConfig.BTC_EXPLORER}
                    onChange={onChange}
                />
                <InputField
                    label="ETH Explorer URL"
                    name="ETH_EXPLORER"
                    value={testConfig.ETH_EXPLORER}
                    onChange={onChange}
                />
                <InputField
                    label="USDT Explorer URL"
                    name="USDT_EXPLORER"
                    value={testConfig.USDT_EXPLORER}
                    onChange={onChange}
                />
                <InputField
                    label="Phone Number (Support)"
                    name="Phone_Number"
                    value={testConfig.Phone_Number}
                    onChange={onChange}
                />
                <InputField
                    label="Sell Gift Card Admin Profit"
                    name="SELL_GIFT_CARD_ADMIN_PROFIT"
                    value={testConfig.SELL_GIFT_CARD_ADMIN_PROFIT}
                    onChange={onChange}
                />
            </TabPanel>

            {/* Bank Settings */}
            <TabPanel value={tabIndex} index={5}>
                <Typography variant="h6">Bank Settings</Typography>
                <InputField
                    label="Account Holder Name"
                    name="ACCOUNT_HOLDER_NAME"
                    value={testConfig.ACCOUNT_HOLDER_NAME}
                    onChange={onChange}
                />
                <InputField
                    label="Account Number"
                    name="ACCOUNT_NUMBER"
                    value={testConfig.ACCOUNT_NUMBER}
                    onChange={onChange}
                />
                <InputField
                    label="Bank Name"
                    name="BANK_NAME"
                    value={testConfig.BANK_NAME}
                    onChange={onChange}
                />
                <InputField
                    label="Minimum Deposit (NGN)"
                    name="MIN_DEPOSIT_NAIRA"
                    value={testConfig.MIN_DEPOSIT_NAIRA}
                    onChange={onChange}
                />
            </TabPanel>
        </Box>
    );
};

export default TestConfig;
